import type * as Monaco from 'monaco-editor'

// Structurizr DSL language definition for Monaco Editor
export const STRUCTURIZR_LANGUAGE_ID = 'structurizr'

// Language configuration (brackets, comments, etc.)
export const structurizrLanguageConfig: Monaco.languages.LanguageConfiguration = {
  comments: {
    lineComment: '//',
    blockComment: ['/*', '*/'],
  },
  brackets: [
    ['{', '}'],
  ],
  autoClosingPairs: [
    { open: '{', close: '}' },
    { open: '"', close: '"' },
  ],
  surroundingPairs: [
    { open: '{', close: '}' },
    { open: '"', close: '"' },
  ],
  folding: {
    markers: {
      start: /^\s*\{/,
      end: /^\s*\}/,
    },
  },
}

// Monarch tokenizer for syntax highlighting
export const structurizrTokensProvider: Monaco.languages.IMonarchLanguage = {
  defaultToken: '',
  ignoreCase: false,

  keywords: [
    'workspace', 'model', 'views', 'configuration',
    'person', 'softwareSystem', 'container', 'component',
    'deploymentEnvironment', 'deploymentNode', 'infrastructureNode',
    'softwareSystemInstance', 'containerInstance', 'instanceOf',
    'group', 'enterprise', 'ref',
    'systemLandscape', 'systemContext', 'filtered', 'dynamic', 'deployment',
    'custom', 'image',
    'styles', 'element', 'relationship', 'themes', 'theme', 'branding', 'terminology',
    'include', 'exclude', 'autoLayout', 'autolayout', 'animation', 'animationStep',
    'title', 'description', 'properties', 'perspectives', 'tags', 'url',
    'shape', 'icon', 'width', 'height', 'background', 'color', 'colour',
    'stroke', 'strokeWidth', 'fontSize', 'border', 'opacity', 'metadata',
    'thickness', 'dashed', 'routing', 'position',
    'impliedRelationships', 'users', 'visibility', 'scope',
    'name', 'logo',
  ],

  directives: [
    '!include', '!docs', '!adrs', '!constant', '!identifiers',
    '!plugin', '!script', '!extend', '!ref',
  ],

  shapes: [
    'Box', 'RoundedBox', 'Circle', 'Ellipse', 'Hexagon', 'Diamond',
    'Cylinder', 'Pipe', 'Person', 'Robot', 'Folder', 'WebBrowser',
    'MobileDevicePortrait', 'MobileDeviceLandscape', 'Component',
  ],

  operators: [
    '->', '=',
  ],

  // Tokenizer rules
  tokenizer: {
    root: [
      // Whitespace
      [/\s+/, 'white'],

      // Comments
      [/#.*$/, 'comment'],
      [/\/\/.*$/, 'comment'],
      [/\/\*/, 'comment', '@comment'],

      // Directives (!include, !constant, etc.)
      [/!(?:include|docs|adrs|constant|identifiers|plugin|script|extend|ref)\b/, 'keyword.directive'],

      // Relationship arrow
      [/->/, 'keyword.operator'],

      // Assignment
      [/=/, 'keyword.operator'],

      // Strings
      [/"([^"\\]|\\.)*$/, 'string.invalid'], // non-terminated string
      [/"/, 'string', '@string'],

      // Variable substitution ${...}
      [/\$\{[^}]+\}/, 'variable'],

      // Braces
      [/[{}]/, 'delimiter.bracket'],

      // Numbers
      [/\d+/, 'number'],

      // Hex colors
      [/#[0-9a-fA-F]{6}\b/, 'number.hex'],
      [/#[0-9a-fA-F]{3}\b/, 'number.hex'],

      // Keywords and identifiers
      [/[a-zA-Z_][\w]*/, {
        cases: {
          '@keywords': 'keyword',
          '@shapes': 'type',
          '@default': 'identifier',
        },
      }],

      // Wildcard
      [/\*/, 'keyword.operator'],
    ],

    string: [
      [/[^\\"]+/, 'string'],
      [/\\./, 'string.escape'],
      [/"/, 'string', '@pop'],
    ],

    comment: [
      [/[^/*]+/, 'comment'],
      [/\*\//, 'comment', '@pop'],
      [/[/*]/, 'comment'],
    ],
  },
}

// Theme colors for Structurizr DSL (light theme)
export const structurizrLightTheme: Monaco.editor.IStandaloneThemeData = {
  base: 'vs',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: '0000FF', fontStyle: 'bold' },
    { token: 'keyword.directive', foreground: '795E26', fontStyle: 'bold' },
    { token: 'keyword.operator', foreground: '000000', fontStyle: 'bold' },
    { token: 'string', foreground: 'A31515' },
    { token: 'string.escape', foreground: 'FF0000' },
    { token: 'comment', foreground: '008000', fontStyle: 'italic' },
    { token: 'identifier', foreground: '001080' },
    { token: 'variable', foreground: '795E26' },
    { token: 'number', foreground: '098658' },
    { token: 'number.hex', foreground: '098658' },
    { token: 'type', foreground: '267F99' },
    { token: 'delimiter.bracket', foreground: '000000' },
  ],
  colors: {},
}

// Theme colors for Structurizr DSL (dark theme)
export const structurizrDarkTheme: Monaco.editor.IStandaloneThemeData = {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: '569CD6', fontStyle: 'bold' },
    { token: 'keyword.directive', foreground: 'DCDCAA', fontStyle: 'bold' },
    { token: 'keyword.operator', foreground: 'D4D4D4', fontStyle: 'bold' },
    { token: 'string', foreground: 'CE9178' },
    { token: 'string.escape', foreground: 'D7BA7D' },
    { token: 'comment', foreground: '6A9955', fontStyle: 'italic' },
    { token: 'identifier', foreground: '9CDCFE' },
    { token: 'variable', foreground: 'DCDCAA' },
    { token: 'number', foreground: 'B5CEA8' },
    { token: 'number.hex', foreground: 'B5CEA8' },
    { token: 'type', foreground: '4EC9B0' },
    { token: 'delimiter.bracket', foreground: 'FFD700' },
  ],
  colors: {},
}

// Register the Structurizr language with Monaco
export function registerStructurizrLanguage(monaco: typeof Monaco) {
  // Register language
  monaco.languages.register({ id: STRUCTURIZR_LANGUAGE_ID })

  // Set language configuration
  monaco.languages.setLanguageConfiguration(STRUCTURIZR_LANGUAGE_ID, structurizrLanguageConfig)

  // Set tokenizer
  monaco.languages.setMonarchTokensProvider(STRUCTURIZR_LANGUAGE_ID, structurizrTokensProvider)

  // Register themes
  monaco.editor.defineTheme('structurizr-light', structurizrLightTheme)
  monaco.editor.defineTheme('structurizr-dark', structurizrDarkTheme)
}
